"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  Slider,
  Paper,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  InputAdornment,
  TextField,
  Select,
  FormControl,
} from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { styled } from "@mui/material/styles";
import { VenueFormData } from "../../../../types/venue";
import apiService from "../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";

// ── types ──────────────────────────────────────────────────────────────────

interface ServiceProvider {
  name: string;
  description: string | null;
}

interface ProviderOption {
  value: string;
  label: string;
}

interface InternetProps {
  formData: VenueFormData;
  handleInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
}

// ── styled components (OUTSIDE component to prevent remounting on each render) ──

const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,

  "& span.required": {
    color: "#ff4d4f",
    marginLeft: "2px",
  },
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      fontSize: "13px",
    },
  }),
);

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "selectbg",
})<{ selectbg?: string }>(({ selectbg }) => ({
  width: "100%",
  backgroundColor: selectbg,
  borderRadius: "30px",
  fontSize: "13px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": { padding: "10px 14px" },
}));

// ── component ──────────────────────────────────────────────────────────────

export default function Internet({
  formData,
  handleInputChange,
}: InternetProps) {
  const [serviceProviders, setServiceProviders] = useState<ProviderOption[]>(
    [],
  );
  const [addProviderOpen, setAddProviderOpen] = useState(false);
  const [newProvider, setNewProvider] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Derive theme colour shortcuts (matching VenueEssentials)
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color400 = reduxTheme.primary_colors?.[400];
  const color900 = reduxTheme.primary_colors?.[900];

  const showRouterDistance = formData.router_accessibility === "yes";

  // ── fetch providers ────────────────────────────────────────────────────

  const fetchProviders = useCallback(async () => {
    try {
      const response = await apiService.get<ServiceProvider[]>(
        "/api/configurations/internet-service-providers/",
      );
      setServiceProviders(
        response.data.map((p) => ({ value: p.name, label: p.name })),
      );
    } catch (error) {
      console.error("Failed to fetch internet service providers:", error);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // ── add provider ───────────────────────────────────────────────────────

  const handleAddProvider = async () => {
    if (!newProvider.trim()) return;
    try {
      await apiService.post("/api/configurations/internet-service-providers/", {
        name: newProvider,
      });
      await fetchProviders();
      setNewProvider("");
      setAddProviderOpen(false);
      showSuccessToast(
        `New service provider "${newProvider}" added successfully!`,
      );
    } catch (error) {
      const errorData = (error as { response?: { data?: { name?: string[] } } })
        ?.response?.data;
      console.error("Failed to add new provider:", error);
      showErrorToast(errorData?.name?.[0] ?? "An error occurred.");
    }
  };

  // ── slider + switch ────────────────────────────────────────────────────

  const handleSliderChange = (_: Event, value: number | number[]) => {
    handleInputChange({
      target: { name: "router_distance_from_livestream", value: String(value) },
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange({
      target: {
        name: "router_accessibility",
        value: e.target.checked ? "yes" : "no",
      },
    });
  };

  // Allow free-typing; accepts any positive integer or empty string while typing
  const handleDistanceInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;
    if (raw === "" || /^\d+$/.test(raw)) {
      handleInputChange({
        target: { name: "router_distance_from_livestream", value: raw },
      });
    }
  };

  const distanceValue =
    parseInt(formData.router_distance_from_livestream || "5") || 5;

  // ── render ─────────────────────────────────────────────────────────────

  return (
    <Box>
      <Grid container spacing={3}>
        {/* ── Internet Service Provider ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FieldLabel textcolor={color100} sx={{ mb: 0 }}>
              Internet Service Provider <span className="required">*</span>
            </FieldLabel>

            <IconButton onClick={() => setAddProviderOpen(true)} size="small">
              <AddCircleOutlineOutlined fontSize="small" />
            </IconButton>
          </Box>

          <FormControl fullWidth sx={{ mt: "6px" }}>
            <StyledSelect
              name="internet_service_provider"
              value={formData.internet_service_provider || ""}
              displayEmpty
              selectbg={color800}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: "internet_service_provider",
                    value: String(e.target.value),
                  },
                })
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select provider...</span>
                )
              }
            >
              {serviceProviders.map((provider, idx) => (
                <MenuItem key={provider.value + idx} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        {/* ── Wifi Name ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Wifi Name</FieldLabel>
          <StyledTextField
            name="wifi_name"
            placeholder="e.g. VenueWifi"
            value={formData.wifi_name || ""}
            onChange={handleInputChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        {/* ── Password ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Password</FieldLabel>
          <StyledTextField
            name="wifi_password"
            type={showPassword ? "text" : "password"}
            placeholder="e.g. •••••"
            value={formData.wifi_password || ""}
            onChange={handleInputChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    sx={{ color: color300, mr: 0.5 }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* ── Upload Speed ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>
            Upload Speed <span className="required">*</span>
          </FieldLabel>
          <StyledTextField
            name="internet_upload_speed"
            placeholder=""
            value={formData.internet_upload_speed || ""}
            onChange={handleInputChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography fontSize="13px" sx={{ color: color700 }}>
                    Mbps
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* ── Router Accessibility ── */}
      <Paper
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 3,
          backgroundColor: color800,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: color900,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WifiIcon sx={{ color: color100 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                fontSize="14px"
                sx={{ color: color100, opacity: 0.9 }}
              >
                Is router accessible
              </Typography>
              <Typography
                variant="body2"
                fontSize="13px"
                sx={{ color: color700 }}
              >
                Whether the router is physically accessible from the livestream
                desk.
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={formData.router_accessibility === "yes"}
            onChange={handleSwitchChange}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: color900,
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: color400,
                opacity: 1,
              },

              "& .MuiSwitch-switchBase.Mui-checked:hover + .MuiSwitch-track": {
                backgroundColor: color400,
              },
            }}
          />
        </Box>
      </Paper>

      {/* ── Router Distance from Livedesk ── */}
      {showRouterDistance && (
        <Box sx={{ mt: 4 }}>
          <FieldLabel textcolor={color100}>
            Router Distance from Livedesk
          </FieldLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {/* Slider — clamps at 100; thumb pins at end if value exceeds max */}
            <Box sx={{ flexGrow: 1 }}>
              <Slider
                value={Math.min(distanceValue, 100)}
                onChange={handleSliderChange}
                min={0}
                max={100}
                sx={{
                  color: color800,
                  "& .MuiSlider-thumb": {
                    backgroundColor: color400,
                    width: 15,
                    height: 15,
                  },
                }}
              />
            </Box>

            {/* Editable distance input */}
            <TextField
              value={formData.router_distance_from_livestream ?? "5"}
              onChange={handleDistanceInputChange}
              size="small"
              inputProps={{
                inputMode: "numeric",
                style: {
                  textAlign: "center",
                  fontSize: "13px",
                  color: color700,
                  padding: "6px 8px",
                  width: "48px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      fontSize="12px"
                      sx={{ color: color300, whiteSpace: "nowrap" }}
                    >
                      m
                    </Typography>
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: color800,
                  borderRadius: "30px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </Box>
      )}

      {/* ── Add Service Provider Dialog ── */}
      <Dialog
        open={addProviderOpen}
        onClose={() => setAddProviderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Service Provider</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FieldLabel textcolor={color100}>Service Provider Name</FieldLabel>
            <StyledTextField
              placeholder="Enter provider name"
              value={newProvider}
              onChange={(e) => setNewProvider(e.target.value)}
              required
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProviderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddProvider}
            variant="contained"
            disabled={!newProvider.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
